// New project: starting fresh (a starter's schema and rules, queued at once) or moving from
// Firebase (the migration kit). Ported from site/dash/main.ts newProject with no change in behaviour.
import { useRef, useState, type FormEvent } from "react";
import { autoId, doc, getDoc, serverTimestamp, writeBatch } from "@estra/client/firestore";
import { STARTERS, VERSION as STARTER_VERSION } from "@estra/create";
import { useConsole } from "../App.tsx";
import { db, errText } from "../api.ts";
import { go, projectHref } from "../router.ts";
import { RESERVED, SLUG_RE, slugify, type Platform } from "../lib/kit.ts";
import { queueFresh } from "./project/files.ts";
import { Choices } from "./Home.tsx";

const PLATS: { id: Platform; label: string }[] = [{ id: "web", label: "Web" }, { id: "ios", label: "iOS" }, { id: "android", label: "Android" }, { id: "server", label: "Servers / scripts" }];
const SIZES = ["Under 10k", "10k–100k", "100k–1M", "1M–10M", "Over 10M"];

export function NewProject({ source }: { source?: "fresh" | "firebase" }) {
  if (source !== "fresh" && source !== "firebase") {
    return (
      <div className="page medium">
        <a className="small muted" href="#/">← All projects</a>
        <div className="hello" style={{ marginTop: 16 }}><span className="kicker">New project</span><h1>Where is your app today?</h1></div>
        <Choices />
      </div>
    );
  }
  return <Form source={source} />;
}

function Form({ source }: { source: "fresh" | "firebase" }) {
  const { user } = useConsole();
  const fresh = source === "fresh";
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugNote, setSlugNote] = useState(".estra.cloud");
  const [touched, setTouched] = useState(false);
  const [fid, setFid] = useState("");
  const [keep, setKeep] = useState(true);
  const [origins, setOrigins] = useState("");
  const [size, setSize] = useState(1e4);
  const [plats, setPlats] = useState<Platform[]>(["web"]);
  const [starter, setStarter] = useState(STARTERS[0]?.id ?? "blank");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const check = useRef(0);

  const checkSlug = async (s: string) => {
    const mine = ++check.current;
    if (!SLUG_RE.test(s)) return setSlugNote("3–30 lowercase letters, digits or dashes");
    if (RESERVED.includes(s)) return setSlugNote("That name is reserved");
    setSlugNote("Checking…");
    const taken = (await getDoc(doc(db, `slugs/${s}`))).exists();
    if (mine === check.current) setSlugNote(taken ? "Taken. Try another." : `✓ ${s}.estra.cloud is yours`);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!SLUG_RE.test(slug) || RESERVED.includes(slug)) return setMsg("Pick a different address.");
    if (!plats.length) return setMsg("Pick at least one platform.");
    setBusy(true);
    const id = autoId();
    const b = writeBatch(db);
    const common = {
      ownerUid: user.uid, name: name.trim(), slug, source, platforms: plats,
      origins: origins.split(/\s+/).map((o) => o.trim().replace(/\/$/, "")).filter(Boolean),
      status: "draft", kit: {}, files: {}, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    };
    b.set(doc(db, `projects/${id}`), fresh
      ? { ...common, starter, starterVersion: STARTER_VERSION, keepFirebaseAuth: false }
      : { ...common, firebaseProjectId: fid.trim(), keepFirebaseAuth: keep, docEstimate: size });
    b.set(doc(db, `slugs/${slug}`), { projectId: id, ownerUid: user.uid, createdAt: serverTimestamp() });
    try {
      await b.commit();
    } catch (err) {
      setMsg((err as { code?: string }).code?.includes("permission-denied") ? `${slug}.estra.cloud is taken or invalid. Pick another address.` : errText(err));
      setBusy(false);
      return;
    }
    // The project offers "Create my backend" again if this part fails.
    if (fresh) await queueFresh(id, starter).catch(() => null);
    go(fresh ? projectHref(id) : projectHref(id, "migrate"));
  };

  return (
    <div className="page medium">
      <a className="small muted" href="#/new">← Start over</a>
      <div className="hello" style={{ marginTop: 16 }}>
        <span className="kicker">{fresh ? "New project · starting fresh" : "New project · moving from Firebase"}</span>
        <h1>{fresh ? "Name it and pick a starter." : "Tell us about your Firebase app."}</h1>
      </div>
      <form className="card form-grid" onSubmit={submit}>
        <label>Project name<input required placeholder={fresh ? "Record shop" : "Acme mobile"} value={name} onChange={(e) => { setName(e.target.value); if (!touched) { const s = slugify(e.target.value); setSlug(s); void checkSlug(s); } }} /></label>
        <label>Backend address
          <span className="affix">
            <input required className="mono" placeholder={fresh ? "record-shop" : "acme"} maxLength={30} value={slug} onChange={(e) => { const s = e.target.value.toLowerCase(); setTouched(true); setSlug(s); void checkSlug(s); }} />
            <span className={`small ${slugNote.startsWith("✓") ? "good" : "muted"}`}>{slugNote}</span>
          </span>
        </label>
        {fresh ? (
          <fieldset><legend>Starter</legend>
            {STARTERS.map((s) => (
              <label key={s.id} className="check"><input type="radio" name="starter" checked={starter === s.id} onChange={() => setStarter(s.id)} /><span><strong>{s.name}</strong><span className="small muted">{s.description}</span></span></label>
            ))}
          </fieldset>
        ) : <label>Firebase project id<input required className="mono" placeholder="acme-prod" value={fid} onChange={(e) => setFid(e.target.value)} /><span className="small muted">From the Firebase console, Project settings.</span></label>}
        <fieldset><legend>Your app runs on</legend>
          <div className="chips">{PLATS.map((pl) => <label key={pl.id} className="chip"><input type="checkbox" checked={plats.includes(pl.id)} onChange={(e) => setPlats(e.target.checked ? [...plats, pl.id] : plats.filter((x) => x !== pl.id))} />{pl.label}</label>)}</div>
        </fieldset>
        {!fresh && <label className="check"><input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /><span><strong>Keep Firebase sign-in for now</strong><span className="small muted">Recommended. Users stay signed in; you move the database first.</span></span></label>}
        <label>Web origins that call your backend (optional)<textarea rows={3} className="mono" placeholder={"https://acme.com\nhttp://localhost:3000"} value={origins} onChange={(e) => setOrigins(e.target.value)} /></label>
        {!fresh && <label>Roughly how many documents?<select value={size} onChange={(e) => setSize(Number(e.target.value))}>{SIZES.map((t, i) => <option key={t} value={[1e4, 1e5, 1e6, 1e7, 1e8][i]}>{t}</option>)}</select></label>}
        <div className="row"><button className="btn" type="submit" disabled={busy}>{fresh ? "Create my backend" : "Create project"}</button>{msg && <span className="small bad">{msg}</span>}</div>
      </form>
    </div>
  );
}
